import React, { useState, useEffect, lazy, Suspense } from "react";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Clock, ChefHat, Heart, Star, Plus, Search, Filter, Beaker, Zap, User, LogOut, BookOpen, Play, CheckCircle, XCircle, Crown, Menu, X, Trash2, ChevronRight, HelpCircle, MessageCircle, Mail, Phone, Scale } from "lucide-react";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthModal from "./components/AuthModal";
import ProtectedRoute from "./components/ProtectedRoute";
import Footer from "./components/Footer";
import OnboardingModal from "./components/OnboardingModal";
import { initGA, trackPageView } from "./utils/analytics";

// Lazy load heavy components for better performance
const RecipeBuilder = lazy(() => import("./components/RecipeBuilder"));
const PaymentModal = lazy(() => import("./components/PaymentModal"));
const SavedRecipesPage = lazy(() => import("./components/SavedRecipesPage"));
const SpecialDietsPage = lazy(() => import("./components/SpecialDietsPage"));
const PrivacyPolicyPage = lazy(() => import("./components/PrivacyPolicyPage"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const AdminBroadcast = lazy(() => import("./components/AdminBroadcast"));
const VetLandingLazy = lazy(() => import("./components/vet/VetPortal").then(m => ({ default: m.VetLanding })));
const VetJoinLazy = lazy(() => import("./components/vet/VetPortal").then(m => ({ default: m.VetJoin })));
const VetLoginLazy = lazy(() => import("./components/vet/VetPortal").then(m => ({ default: m.VetLogin })));
const VetDashboardLazy = lazy(() => import("./components/vet/VetPortal").then(m => ({ default: m.VetDashboard })));
const VetRecipeBuilderLazy = lazy(() => import("./components/vet/VetPortal").then(m => ({ default: m.VetRecipeBuilder })));
const VetPreviewLazy = lazy(() => import("./components/vet/VetPortal").then(m => ({ default: m.VetPreview })));
const VetDemoLazy = lazy(() => import("./components/vet/VetPortal").then(m => ({ default: m.VetDemo })));
const AdminVetDemosLazy = lazy(() => import("./components/AdminVetDemos"));
const AdminVetRecipesLazy = lazy(() => import("./components/AdminVetRecipes"));
const KibblePortionCalculatorLazy = lazy(() => import("./components/KibblePortionCalculator"));
const KibbleSEOPageLazy = lazy(() => import("./components/KibbleSEOPage"));
const SimpleAdminTest = lazy(() => import("./components/SimpleAdminTest"));
const ProtectedAdminRoute = lazy(() => import("./components/ProtectedAdminRoute"));
const BlogPage = lazy(() => import("./components/BlogPage"));
const BlogPost = lazy(() => import("./components/BlogPost"));
const HowItWorks = lazy(() => import("./components/HowItWorks"));
const QASection = lazy(() => import("./components/QASection"));
const PetNutritionCalculator = lazy(() => import("./components/PetNutritionCalculator"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const TermsOfService = lazy(() => import("./components/TermsOfService"));
const FAQPage = lazy(() => import("./components/FAQPage"));
const GoogleAuthCallback = lazy(() => import("./components/GoogleAuthCallback"));
const MealPlannerPreview = lazy(() => import("./components/MealPlannerPreview"));
const MealPlanOrder = lazy(() => import("./components/MealPlanOrder"));
const MealPlanSuccess = lazy(() => import("./components/MealPlanSuccess"));


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = ({ showAuth = true, setShowOnboarding }) => {
  const { user, logout, openAuthModal } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <nav className="bg-white/90 backdrop-blur-sm border-b border-green-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 sm:h-24 md:h-28 lg:h-32">
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_desipet-recipes/artifacts/zxt1m4vn_Logo.png" 
              alt="Desi Pet Recipes - AAFCO Compliant Pet Food Recipes" 
              className="h-16 sm:h-20 md:h-24 lg:h-28 w-auto object-contain"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              width="200"
              height="112"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-3 flex-shrink-0">
            <Button asChild variant="outline" size="sm" className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800">
              <Link to="/blog">Blog</Link>
            </Button>
            
            <Button 
              asChild 
              variant="outline" 
              size="sm" 
              className="border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800 font-semibold"
            >
              <Link to="/recipe-book" className="flex items-center">
                <BookOpen className="w-4 h-4 mr-2" />
                Buy Recipe Books
              </Link>
            </Button>
            
            <Button 
              asChild
              variant="outline" 
              size="sm" 
              className="border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
            >
              <Link to="/faq">FAQ</Link>
            </Button>

            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
            >
              <Link to="/kibble-calculator" data-testid="nav-kibble-calc">
                <Scale className="w-4 h-4 mr-1" /> Kibble Calc <span className="ml-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">FREE</span>
              </Link>
            </Button>
            
            {setShowOnboarding && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowOnboarding(true)}
                className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
              >
                <Play className="w-4 h-4 mr-2" />
                How it Works
              </Button>
            )}
            
            {showAuth && user ? (
              <>
                <Button asChild variant="outline" size="sm" className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800">
                  <Link to="/pets" className="flex items-center">
                    <Heart className="w-4 h-4 mr-2" />
                    My Pets
                  </Link>
                </Button>
                
                <Button asChild variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800">
                  <Link to="/special-diets" className="flex items-center">
                    <Beaker className="w-4 h-4 mr-2" />
                    Special Diets
                  </Link>
                </Button>
                
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 hidden lg:block">
                    Hi <span className="font-medium text-green-700">{user.name}</span>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={logout}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Logout
                  </Button>
                </div>
              </>
            ) : showAuth ? (
              <div className="flex items-center space-x-2">
                <Button 
                  variant="ghost"
                  onClick={() => openAuthModal('login')}
                  className="text-green-700 hover:text-green-900 hover:bg-green-50"
                  data-testid="nav-login-btn"
                >
                  <User className="w-4 h-4 mr-1" />
                  Sign In
                </Button>
                <Button 
                  onClick={() => openAuthModal('signup')}
                  className="bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
                  data-testid="nav-signup-btn"
                >
                  Get Started
                </Button>
              </div>
            ) : (
              <Button asChild className="bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600">
                <Link to="/pets" data-testid="get-started-btn">Get Started</Link>
              </Button>
            )}
          </div>
          
          {/* Mobile Sign In Button + Hamburger */}
          <div className="md:hidden flex items-center gap-2">
            {showAuth && !user && (
              <Button 
                size="sm"
                onClick={() => openAuthModal('login')}
                className="bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600 text-white"
                data-testid="mobile-signin-btn"
              >
                <User className="w-4 h-4 mr-1" />
                Sign In
              </Button>
            )}
            
            <button
              className="p-2 rounded-md hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-3 space-y-2 border-t border-gray-100">
            <Link to="/blog" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md" onClick={() => setMobileMenuOpen(false)}>
              📝 Blog
            </Link>
            
            <Link 
              to="/faq" 
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md" 
              onClick={() => setMobileMenuOpen(false)}
            >
              💬 FAQ
            </Link>
            
            <Link to="/recipe-book" className="block px-4 py-2 text-sm text-orange-700 hover:bg-orange-50 rounded-md font-semibold" onClick={() => setMobileMenuOpen(false)}>
              <BookOpen className="w-4 h-4 inline mr-2" />
              Buy Recipe Books
            </Link>

            <Link to="/kibble-calculator" className="block px-4 py-2 text-sm text-orange-700 hover:bg-orange-50 rounded-md font-semibold" onClick={() => setMobileMenuOpen(false)}>
              <Scale className="w-4 h-4 inline mr-2" />
              Kibble Calculator <span className="ml-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">FREE</span>
            </Link>
            
            {setShowOnboarding && (
              <button
                onClick={() => {
                  setShowOnboarding(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
              >
                <Play className="w-4 h-4 inline mr-2" />
                How it Works
              </button>
            )}
            
            {showAuth && user ? (
              <>
                <Link to="/pets" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md" onClick={() => setMobileMenuOpen(false)}>
                  <Heart className="w-4 h-4 inline mr-2" />
                  My Pets
                </Link>
                
                <Link to="/special-diets" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md" onClick={() => setMobileMenuOpen(false)}>
                  <Beaker className="w-4 h-4 inline mr-2" />
                  Special Diets
                </Link>
                
                <div className="px-4 py-2 text-xs text-gray-600 border-t border-gray-100">
                  Hi <span className="font-medium text-green-700">{user.name}</span>
                </div>
                
                <button
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md"
                >
                  <LogOut className="w-4 h-4 inline mr-2" />
                  Logout
                </button>
              </>
            ) : showAuth ? (
              <>
                <button
                  onClick={() => {
                    openAuthModal('login');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 rounded-md font-medium"
                >
                  <User className="w-4 h-4 inline mr-2" />
                  Sign In
                </button>
                
                <button
                  onClick={() => {
                    openAuthModal('signup');
                    setMobileMenuOpen(false);
                  }}
                  className="mx-4 px-4 py-2 text-sm bg-gradient-to-r from-green-500 to-orange-500 text-white rounded-md font-medium"
                >
                  Get Started
                </button>
              </>
            ) : (
              <Link to="/pets" className="mx-4 block px-4 py-2 text-sm bg-gradient-to-r from-green-500 to-orange-500 text-white rounded-md text-center font-medium" onClick={() => setMobileMenuOpen(false)}>
                Get Started
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

// Landing Page
const LandingPage = ({ setShowOnboarding }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-orange-50 to-yellow-50">
      <Helmet>
        {/* Bug 1 Fix: Unique, compelling title with brand and key benefits */}
        <title>Desi Pet Recipes | AAFCO-Compliant Homemade Pet Food | Save ₹8000/Year with Indian Ingredients</title>
        
        {/* Bug 1 Fix: Detailed, unique meta description with CTA */}
        <meta name="description" content="Create scientifically-balanced homemade pet food recipes using affordable Indian ingredients like dal, rice & vegetables. AAFCO-compliant nutrition for dogs & cats. Save ₹8000 annually. Get personalized recipes now!" />
        
        <meta name="keywords" content="homemade pet food recipes India, AAFCO pet nutrition, Indian dog food recipes, cat food recipes dal rice, pet food cost savings India, desi pet food, healthy pet recipes Indian ingredients" />
        <link rel="canonical" href="https://desipetrecipes.in/" />
        
        {/* Bug 3 Fix: Complete Open Graph tags for social sharing */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://desipetrecipes.in/" />
        <meta property="og:title" content="Desi Pet Recipes - AAFCO-Compliant Homemade Pet Food with Indian Ingredients" />
        <meta property="og:description" content="Create healthy, affordable homemade pet food using Indian ingredients. Save ₹8000/year with scientifically-balanced AAFCO recipes for dogs & cats." />
        <meta property="og:image" content="https://desipetrecipes.in/og-image-home.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Desi Pet Recipes" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://desipetrecipes.in/" />
        <meta name="twitter:title" content="Desi Pet Recipes - AAFCO-Compliant Homemade Pet Food" />
        <meta name="twitter:description" content="Save ₹8000/year on pet food with healthy homemade recipes using Indian ingredients." />
        <meta name="twitter:image" content="https://desipetrecipes.in/og-image-home.jpg" />
      </Helmet>
      <Navigation setShowOnboarding={setShowOnboarding} />

      {/* Hero Section with Integrated Calculator */}
      <section className="pt-12 sm:pt-20 pb-8 sm:pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-6 lg:gap-8 items-start">
            
            {/* Pet Nutrition Calculator - Shows FIRST on mobile, second on desktop */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              {/* Getting Started Guide - Clear User Flow */}
              <div className="mb-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-4 text-white shadow-lg">
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <span className="text-2xl">🚀</span> Start Here - 3 Easy Steps!
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 bg-white/10 rounded-lg p-2">
                    <span className="flex-shrink-0 w-7 h-7 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">1</span>
                    <span className="text-sm">Fill pet details below <span className="text-yellow-300 font-semibold">👇</span></span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/10 rounded-lg p-2">
                    <span className="flex-shrink-0 w-7 h-7 bg-white/30 text-white rounded-full flex items-center justify-center font-bold text-sm">2</span>
                    <span className="text-sm opacity-80">Quick sign-up (30 seconds)</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/10 rounded-lg p-2">
                    <span className="flex-shrink-0 w-7 h-7 bg-white/30 text-white rounded-full flex items-center justify-center font-bold text-sm">3</span>
                    <span className="text-sm opacity-80">Create unlimited free recipes! 🎉</span>
                  </div>
                </div>
              </div>
              <PetNutritionCalculator />
            </div>
            
            {/* Hero Image with Text Overlay - Desktop Only */}
            <div className="lg:col-span-3 order-2 lg:order-1 hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                {/* Hero Image */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_desipetrcp/artifacts/crsv4174_WhatsApp%20Image%202025-11-24%20at%2020.13.37.jpeg"
                  alt="Woman preparing fresh pet food with golden retriever"
                  className="w-full h-[500px] object-cover"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  width="1200"
                  height="500"
                />
                
                {/* Gradient Overlay for better text visibility */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
                
                {/* Hero Text Overlay */}
                <div className="absolute inset-0 flex flex-col justify-center p-8 lg:p-12">
                  <div className="space-y-4 max-w-xl">
                    <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-200 text-sm px-4 py-1.5 inline-block">
                      ❤️ Made In India, Made for the World, Made for Pets
                    </Badge>
                    <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                      <span className="bg-gradient-to-r from-green-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                        100% Free
                      </span>
                      <br />
                      <span className="text-white">Unlimited Pet Recipes</span>
                      <br />
                      <span className="text-gray-200">Made Simple</span>
                    </h1>
                    <p className="text-lg text-gray-100 leading-relaxed">
                      Create unlimited AAFCO-compliant recipes tailored to your pet's unique needs. Completely free, forever!
                    </p>
                    
                    <div className="pt-4">
                      <Button asChild size="lg" className="bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600 text-base px-8 py-6 shadow-lg hover:shadow-xl transition-all">
                        <Link to="/pets" data-testid="create-pet-profile-btn">
                          <span className="flex items-center gap-2">
                            Create Pet Profile
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </span>
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>
      <section className="py-12 sm:py-16 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <Badge className="bg-green-100 text-green-700 border-green-200 text-base px-6 py-2 mb-4">
              ✨ Completely FREE Platform - No Subscriptions, No Hidden Costs
            </Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-4">Why Choose Desi Pet Recipes?</h2>
            <p className="text-lg sm:text-xl text-gray-600">Authentic Indian nutrition meets modern pet science</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-green-50 to-white">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl font-bold text-green-800">Free Unlimited Recipes</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">Create as many recipes as you want! Calculate exact daily requirements based on your pet's unique profile.</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-orange-50 to-white">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <ChefHat className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl font-bold text-orange-800">Indian Ingredients</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">Recipes using locally available ingredients like dal, rice, vegetables, and spices safe for pets.</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-yellow-50 to-white">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl font-bold text-yellow-800">AI Recommendations</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">Smart recipe suggestions based on your pet's health conditions and dietary preferences.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Veterinarian Credential Badge */}
      <section className="py-8 bg-gradient-to-r from-green-50 via-blue-50 to-green-50 border-y border-green-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center">
            <div className="bg-white px-6 py-4 rounded-full shadow-lg border-2 border-green-500 flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 to-blue-500 rounded-full">
                <span className="text-white text-2xl">🩺</span>
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500 font-medium">Trusted by Pet Parents</p>
                <p className="text-base font-bold text-gray-800">Designed & Developed by a Veterinarian</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* User Journey Section - Clear Steps */}
      <section className="py-16 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              🎯 Your Journey to Better Pet Nutrition
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Follow these simple steps to give your pet the best homemade nutrition
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {/* Step 1 */}
            <Card className="text-center border-2 border-blue-200 hover:shadow-xl transition-all hover:scale-105 bg-white">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">1</span>
                </div>
                <div className="text-4xl mb-3">🧮</div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Use Calculator</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Try our Pet Nutrition Calculator to understand your pet's daily needs
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  onClick={() => document.querySelector('.max-w-7xl')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Try Calculator →
                </Button>
              </CardContent>
            </Card>
            
            {/* Step 2 */}
            <Card className="text-center border-2 border-green-200 hover:shadow-xl transition-all hover:scale-105 bg-white">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">2</span>
                </div>
                <div className="text-4xl mb-3">🐕</div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Create Profile</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Make a detailed profile for your pet (age, weight, breed, activity)
                </p>
                <Button 
                  asChild
                  size="sm" 
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                >
                  <Link to="/pets">Create Profile →</Link>
                </Button>
              </CardContent>
            </Card>
            
            {/* Step 3 */}
            <Card className="text-center border-2 border-orange-200 hover:shadow-xl transition-all hover:scale-105 bg-white">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">3</span>
                </div>
                <div className="text-4xl mb-3">🍳</div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Create Unlimited Recipes</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Build AAFCO-compliant recipes using Indian ingredients - 100% FREE!
                </p>
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  ✨ Always Free
                </Badge>
              </CardContent>
            </Card>
            
            {/* Step 4 */}
            <Card className="text-center border-2 border-purple-200 hover:shadow-xl transition-all hover:scale-105 bg-white">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">4</span>
                </div>
                <div className="text-4xl mb-3">📚</div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Buy Recipe Book</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Optional: Get 50 ready-made recipes in PDF format (₹149 only)
                </p>
                <Button 
                  asChild
                  size="sm" 
                  variant="outline"
                  className="border-purple-500 text-purple-600 hover:bg-purple-50"
                >
                  <Link to="/recipe-book">View Books →</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
          
          <div className="mt-12 text-center">
            <Card className="inline-block bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300">
              <CardContent className="p-6">
                <p className="text-gray-800 font-medium">
                  <span className="text-2xl mr-2">🎉</span>
                  Platform is <span className="font-bold text-green-600">100% FREE</span> forever! 
                  Create unlimited recipes at no cost.
                  <span className="text-2xl ml-2">🎉</span>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Premium Products — Recipe Book (₹149) + 7-Day Meal Plan (₹499) */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-orange-100 text-orange-800 border-orange-200 mb-3">
              ⭐ Premium Add-ons — one-time payments, yours forever
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              Take it a step further
            </h2>
            <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
              The platform is 100% free — but if you want done-for-you recipes or a personalised weekly plan, we have two paid options.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Recipe Book Card */}
            <Card className="overflow-hidden border-2 border-purple-200 hover:border-purple-400 hover:shadow-2xl transition-all bg-white">
              <div className="bg-gradient-to-br from-purple-600 to-indigo-600 p-5 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-6 h-6" />
                    <span className="text-xs font-bold tracking-wider uppercase opacity-90">Ready-made</span>
                  </div>
                  <Badge className="bg-white/20 text-white border-0">50% OFF</Badge>
                </div>
                <h3 className="text-xl sm:text-2xl font-extrabold mt-3">Recipe Book — 50 Vet Recipes</h3>
                <p className="text-white/85 text-sm mt-1">Instant PDF · Indian ingredients · AAFCO-aligned</p>
              </div>
              <CardContent className="p-5">
                <ul className="space-y-2 text-sm text-gray-700 mb-4">
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" /> 50 curated recipes across breakfast, lunch, dinner, treats</li>
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" /> Portion charts by pet weight</li>
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" /> Download instantly, cook anytime</li>
                </ul>
                <div className="flex items-center justify-between border-t pt-4">
                  <div>
                    <span className="text-gray-400 line-through text-sm mr-2">₹299</span>
                    <span className="text-3xl font-extrabold text-purple-700">₹149</span>
                    <span className="text-gray-500 text-xs ml-1">one-time</span>
                  </div>
                  <Button asChild className="bg-purple-600 hover:bg-purple-700 text-white font-bold">
                    <Link to="/recipe-book" data-testid="product-recipe-book-btn">
                      Buy Recipe Book <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* 7-Day Meal Plan Card */}
            <Card className="overflow-hidden border-2 border-orange-300 hover:border-orange-500 hover:shadow-2xl transition-all bg-white relative">
              <div className="absolute top-3 right-3 z-10">
                <Badge className="bg-yellow-400 text-orange-900 hover:bg-yellow-400 border-0 font-bold shadow">MOST POPULAR</Badge>
              </div>
              <div className="bg-gradient-to-br from-orange-600 to-red-600 p-5 text-white">
                <div className="flex items-center gap-2">
                  <ChefHat className="w-6 h-6" />
                  <span className="text-xs font-bold tracking-wider uppercase opacity-90">Personalised</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-extrabold mt-3">7-Day Meal Plan (custom)</h3>
                <p className="text-white/85 text-sm mt-1">Rotating recipes + shopping list · emailed as PDF</p>
              </div>
              <CardContent className="p-5">
                <ul className="space-y-2 text-sm text-gray-700 mb-4">
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" /> Calibrated to your pet's breed, age, weight, conditions</li>
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" /> Pure homemade OR 30/70 · 50/50 hybrid with kibble</li>
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" /> Consolidated shopping list with retailer links</li>
                </ul>
                <div className="flex items-center justify-between border-t pt-4">
                  <div>
                    <span className="text-gray-400 line-through text-sm mr-2">₹999</span>
                    <span className="text-3xl font-extrabold text-orange-700">₹499</span>
                    <span className="text-gray-500 text-xs ml-1">one-time</span>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white font-bold">
                      <Link to="/meal-plan-order" data-testid="product-meal-plan-btn">
                        Get my plan <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                    <Link to="/meal-planner-preview"
                          className="text-xs text-orange-700 hover:text-orange-800 font-semibold underline underline-offset-2"
                          data-testid="product-meal-plan-preview">
                      See sample plan →
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Q&A Section removed - users directed to /faq page instead */}

      {/* Comparison Table Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-4">
              Why Choose DesiRecipe.in Over Generic AI?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              See how our specialized pet nutrition platform compares to generic AI solutions
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                  <tr>
                    <th className="px-4 sm:px-6 py-4 text-left font-semibold text-sm sm:text-base">Feature</th>
                    <th className="px-4 sm:px-6 py-4 text-left font-semibold text-sm sm:text-base">
                      <span className="flex items-center">
                        <Heart className="w-5 h-5 mr-2" />
                        DesiRecipe.in
                      </span>
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-left font-semibold text-sm sm:text-base">ChatGPT / Generic AI</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 font-semibold text-gray-800 text-sm sm:text-base">Verified Nutrition</td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-700">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Recipes <strong>checked against AAFCO requirements</strong> with detailed nutrient breakdown (protein, fat, carbs, calories). Shows nutritional gaps to meet standards.</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-600">
                      <div className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Gives only <strong>approximate guidance</strong>, not detailed nutrient breakdown or AAFCO compliance checking.</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 font-semibold text-gray-800 text-sm sm:text-base">India-Focused Ingredients</td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-700">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Comprehensive <strong>Indian ingredient database</strong> (dal, basmati rice, Indian vegetables, regional proteins) with nutrition data and Hindi names.</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-600">
                      <div className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Uses mostly <strong>Western ingredient assumptions</strong>, no local ingredient database or regional availability.</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 font-semibold text-gray-800 text-sm sm:text-base">Professional Credibility</td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-700">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Built by <strong>veterinary professionals</strong> with AAFCO-based formulations. Recipes include veterinary consultation recommendations.</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-600">
                      <div className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Anonymous AI model with no veterinary accountability or professional oversight.</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 font-semibold text-gray-800 text-sm sm:text-base">Downloadable Reports</td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-700">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span><strong>Professional PDF export</strong> with complete recipe details, nutritional breakdown, and feeding instructions.</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-600">
                      <div className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>No downloadable reports or formatted output for professional use.</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 font-semibold text-gray-800 text-sm sm:text-base">Pet Profile Management</td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-700">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span><strong>Stores detailed pet profiles</strong> (breed, age, weight, activity) and saved recipes for each pet with personalized nutrition calculations.</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-600">
                      <div className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>No memory between conversations, no individual pet profiles or saved data.</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 font-semibold text-gray-800 text-sm sm:text-base">AAFCO Guidelines Based</td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-700">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span><strong>Uses AAFCO nutrient standards</strong> for recipe formulation and validation, suitable for informed home feeding decisions.</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-600">
                      <div className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>No structured nutritional standards or regulatory framework reference.</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 font-semibold text-gray-800 text-sm sm:text-base">Professional Guidance</td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-700">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Includes <strong>veterinary consultation recommendations</strong> and therapeutic diet precautions for health conditions.</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-600">
                      <div className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Generic responses without professional medical guidance or safety recommendations.</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 font-semibold text-gray-800 text-sm sm:text-base">Nutritional Transparency</td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-700">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Displays <strong>detailed nutrient calculations</strong> (protein, fat, carbs, calories) with AAFCO requirement comparisons and gap analysis.</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm sm:text-base text-gray-600">
                      <div className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Text-based suggestions without specific nutrient calculations or quantified analysis.</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Button asChild size="lg" className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold px-8 py-3 text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <Link to="/pets">Get Started with Professional Pet Nutrition</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

// Recipe Book Purchase Flow Component
const RecipeBookPurchaseFlow = ({ pet, onClose }) => {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  const versions = [
    {
      id: "indian",
      name: "Indian Version",
      price: "₹149",
      description: "50 recipes using Indian ingredients like dal, rice, chicken, vegetables",
      features: ["Local ingredients", "Hindi names included", "Regional availability"]
    },
    {
      id: "international",
      name: "International Version",
      price: "$2.99 USD",
      description: "50 recipes with international ingredients commonly available globally",
      features: ["Global ingredients", "Standard measurements", "Wide availability"]
    }
  ];
  
  const handlePurchase = async () => {
    if (!selectedVersion || !pet) return;
    
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      
      // Create Razorpay order
      const response = await axios.post(`${API}/recipe-book/create-order`, {
        version: selectedVersion,
        pet_id: pet.id,
        origin_url: originUrl
      });
      
      const { order_id, amount, currency, key_id, user_name, user_email, user_phone } = response.data;
      
      // Initialize Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "Desi Pet Recipes",
        description: `50 Recipe Book for ${pet.name}`,
        order_id: order_id,
        handler: async function (razorpayResponse) {
          try {
            // Debug: Log what Razorpay returns
            console.log('🔍 Razorpay Response:', razorpayResponse);
            console.log('🔍 Response keys:', Object.keys(razorpayResponse));
            
            // Get fresh token from localStorage to ensure it's available
            const token = localStorage.getItem('access_token');
            if (!token) {
              console.error('❌ No authentication token found in localStorage');
              throw new Error('Authentication token not found. Please login again.');
            }
            
            console.log('✅ Token found, length:', token.length);
            
            // Validate required fields from Razorpay
            if (!razorpayResponse.razorpay_order_id || !razorpayResponse.razorpay_payment_id || !razorpayResponse.razorpay_signature) {
              console.error('❌ Missing required fields from Razorpay:', {
                has_order_id: !!razorpayResponse.razorpay_order_id,
                has_payment_id: !!razorpayResponse.razorpay_payment_id,
                has_signature: !!razorpayResponse.razorpay_signature
              });
              
              // Show user-friendly error
              toast.error('Payment verification incomplete. Please contact support with your payment details.', {
                duration: 8000
              });
              
              throw new Error('Incomplete payment data received from Razorpay');
            }
            
            // Prepare payload
            const payloadData = {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature
            };
            
            console.log('📤 Sending payment verification:', payloadData);
            
            // Verify payment on backend with explicit Authorization header
            const verifyResponse = await axios.post(`${API}/recipe-book/verify-payment`, payloadData, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            console.log('✅ Verification response:', verifyResponse.data);
            
            // Show success message
            toast.success('🎉 Payment successful! Your recipe book is ready!', {
              duration: 5000,
              position: 'top-center'
            });
            
            // Small delay to show success message, then redirect to My Purchases
            setTimeout(() => {
              navigate('/my-purchases');
              toast.success('📥 Click the download button to get your recipe book!', {
                duration: 8000,
                position: 'top-center'
              });
            }, 2000);
            
          } catch (error) {
            console.error('Payment verification error:', error);
            
            // Show specific error message if available
            const errorMessage = error.response?.data?.detail || error.message || 'Payment verification failed';
            toast.error(`${errorMessage}. Please check My Purchases page or contact support.`, {
              duration: 6000
            });
            
            // Still redirect to My Purchases in case payment succeeded but verification had issues
            setTimeout(() => navigate('/my-purchases'), 3000);
          }
        },
        prefill: {
          name: user_name,
          email: user_email,
          contact: user_phone
        },
        theme: {
          color: "#f97316" // Orange color matching the app theme
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            toast.info('Payment cancelled');
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setLoading(false);
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout. Please try again.');
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {versions.map((version) => (
          <div
            key={version.id}
            onClick={() => setSelectedVersion(version.id)}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              selectedVersion === version.id
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-200 hover:border-orange-300'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedVersion === version.id
                    ? 'border-orange-500 bg-orange-500'
                    : 'border-gray-300'
                }`}>
                  {selectedVersion === version.id && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <h3 className="font-bold text-gray-800">{version.name}</h3>
              </div>
              <span className="text-lg font-bold text-orange-600">{version.price}</span>
            </div>
            <p className="text-sm text-gray-600 mb-2">{version.description}</p>
            <div className="flex flex-wrap gap-2">
              {version.features.map((feature, idx) => (
                <span key={idx} className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-700">
                  {feature}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className="bg-blue-50 p-3 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>✨ What you'll get:</strong>
        </p>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>• 50 personalized recipes for {pet?.name}</li>
          <li>• Professional PDF with pet's name</li>
          <li>• AAFCO-compliant nutrition info</li>
          <li>• Instant download after payment</li>
        </ul>
      </div>
      
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={onClose}
          className="flex-1"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handlePurchase}
          disabled={!selectedVersion || loading}
          className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing...
            </>
          ) : (
            'Proceed to Payment'
          )}
        </Button>
      </div>
    </div>
  );
};

// Pet Management Page  
const PetManagementPage = () => {
  const { subscription, usageStats, getRemainingRecipes, user, loading: authLoading } = useAuth();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [hasPurchasedBooks, setHasPurchasedBooks] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    pet_type: '',
    breed: '',
    age_months: '',
    weight_kg: '',
    activity_level: '',
    health_conditions: []
  });

  // Scroll to pet card after signup redirect
  useEffect(() => {
    const shouldScroll = sessionStorage.getItem('scrollToPetCard');
    if (shouldScroll && !loading && pets.length > 0) {
      sessionStorage.removeItem('scrollToPetCard');
      // Scroll to the pet cards section (middle of page)
      setTimeout(() => {
        const petCardsSection = document.querySelector('[data-testid="pet-cards-section"]');
        if (petCardsSection) {
          petCardsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Fallback: scroll to a reasonable position
          window.scrollTo({ top: 300, behavior: 'smooth' });
        }
      }, 300);
    }
  }, [loading, pets]);

  useEffect(() => {
    // Only fetch pets after auth is loaded and user is authenticated
    if (!authLoading && user) {
      fetchPets();
      fetchPurchases();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [authLoading, user]);

  const fetchPets = async () => {
    try {
      const response = await axios.get(`${API}/pets`);
      setPets(response.data);
    } catch (error) {
      toast.error('Failed to fetch pets');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/recipe-book/my-purchases`);
      // Check if user has any completed purchases
      const completedPurchases = response.data.filter(p => p.payment_status === 'completed');
      setHasPurchasedBooks(completedPurchases.length > 0);
    } catch (error) {
      // Silently fail - user just doesn't have purchases
      setHasPurchasedBooks(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate weight based on pet type
      const weight = parseFloat(formData.weight_kg);
      const maxWeight = formData.pet_type === 'cat' ? 8 : 100;
      
      if (weight <= 0 || weight > maxWeight) {
        const maxWeightText = formData.pet_type === 'cat' ? '8 kg' : '100 kg';
        toast.error(`Please enter a valid weight between 0.1 and ${maxWeightText}`);
        return;
      }
      
      const petData = {
        ...formData,
        age_months: parseInt(formData.age_months),
        weight_kg: weight
      };
      
      await axios.post(`${API}/pets`, petData);
      toast.success('Pet profile created successfully!');
      setShowAddForm(false);
      setFormData({
        name: '',
        pet_type: '',
        breed: '',
        age_months: '',
        weight_kg: '',
        activity_level: '',
        health_conditions: []
      });
      fetchPets();
    } catch (error) {
      toast.error('Failed to create pet profile');
    }
  };

  const handleDeletePet = async (petId, petName) => {
    // Confirm deletion
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${petName}'s profile? This will also delete all associated recipes and cannot be undone.`
    );
    
    if (!confirmDelete) {
      return;
    }
    
    try {
      await axios.delete(`${API}/pets/${petId}`);
      toast.success(`${petName}'s profile deleted successfully`);
      fetchPets(); // Refresh the pet list
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete pet profile';
      toast.error(errorMessage);
    }
  };

  const fetchRequirements = async (petId) => {
    try {
      const response = await axios.get(`${API}/pets/${petId}/aafco-requirements`);
      setRequirements(response.data);
    } catch (error) {
      toast.error('Failed to fetch nutritional requirements');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your pets...</p>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute>
      <>
        <Helmet>
          <title>My Pets | Manage Pet Profiles | Desi Pet Recipes</title>
          {/* Bug 25 Fix: Enhanced unique meta description */}
          <meta name="description" content="Create and manage detailed pet profiles for your dogs and cats. Track nutrition, generate AAFCO-compliant homemade recipes, monitor health metrics, and save custom meal plans. Your personal pet nutrition dashboard." />
          <meta name="keywords" content="pet profiles, dog nutrition tracking, cat meal plans, pet health dashboard, AAFCO recipes, homemade pet food management" />
          {/* Bug 13 Fix: Add canonical URL */}
          <link rel="canonical" href="https://desipetrecipes.in/pets" />
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50" data-testid="pet-management-page">
          <Navigation />

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Free Platform Banner */}
          <div className="mb-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🎉</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold">100% Free Platform - Create Unlimited Recipes!</h3>
                  <p className="text-sm text-green-50">No subscriptions, no hidden costs. Just pure pet nutrition.</p>
                </div>
              </div>
              {hasPurchasedBooks && (
                <Button
                  asChild
                  variant="outline"
                  className="bg-white text-green-600 hover:bg-green-50 border-0"
                >
                  <Link to="/recipe-book">
                    <BookOpen className="w-4 h-4 mr-2" />
                    View Recipe Books
                  </Link>
                </Button>
              )}
            </div>
          </div>
          
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800">My Pet Profiles</h1>
          <Button 
            onClick={() => setShowAddForm(true)}
            className="bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
            data-testid="add-pet-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Pet
          </Button>
        </div>

        {/* Remove old subscription status card */}
        {false && subscription && (
          <Card className={`mb-6 ${subscription.plan === 'premium' ? 'border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50' : 'border-blue-200 bg-blue-50'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {subscription.plan === 'premium' ? (
                    <>
                      <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-yellow-800">Premium Plan Active</p>
                        <p className="text-sm text-yellow-600">Unlimited recipes and AI recommendations</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <Heart className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-blue-800">Free Plan</p>
                        <p className="text-sm text-blue-600">
                          Saved recipes: {usageStats?.saved_recipes_count || 0}/2 • 
                          Remaining: {getRemainingRecipes()}
                        </p>
                      </div>
                    </>
                  )}
                </div>
                {subscription.plan === 'free' && (
                  <Button 
                    onClick={() => setShowPaymentModal(true)}
                    className="bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade ₹99/month
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {pets.length === 0 ? (
          <Card className="text-center p-8">
            <CardContent>
              <img 
                src="https://images.unsplash.com/photo-1450778869180-41d0601e046e" 
                alt="Cute pets"
                className="w-64 h-48 object-cover rounded-lg mx-auto mb-6"
              />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No pets added yet</h3>
              <p className="text-gray-600 mb-4">Create your first pet profile to get personalized recipes and nutrition plans.</p>
              <Button 
                onClick={() => setShowAddForm(true)}
                className="bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
                data-testid="add-first-pet-btn"
              >
                Add Your First Pet
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Helpful Guide Banner for New Users */}
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500 p-4 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-blue-800">👋 New here? Start creating recipes!</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Click the <span className="font-bold text-purple-600">"Create Recipe"</span> button on your pet's card to build AAFCO-compliant meals using Indian ingredients!
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pet-cards-section">
              {pets.map((pet) => (
                <Card key={pet.id} className="hover:shadow-lg transition-shadow" data-testid={`pet-card-${pet.id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{pet.name}</CardTitle>
                    <Badge className={pet.pet_type === 'dog' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                      {pet.pet_type === 'dog' ? '🐕 Dog' : '🐱 Cat'}
                    </Badge>
                  </div>
                  <CardDescription>
                    {pet.breed} • {Math.floor(pet.age_months / 12)}y {pet.age_months % 12}m • {pet.weight_kg}kg
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Life Stage:</span>
                    <Badge variant="outline">{pet.life_stage.replace('_', ' ')}</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Activity:</span>
                    <span className="font-medium capitalize">{pet.activity_level}</span>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        setSelectedPet(pet);
                        fetchRequirements(pet.id);
                      }}
                      data-testid={`view-nutrition-${pet.id}`}
                    >
                      View Daily Nutrition for {pet.name}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="relative col-span-2 mb-2">
                      <Button 
                        asChild
                        size="lg" 
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
                      >
                        <Link to={`/recipe-builder/${pet.id}`} data-testid={`recipe-builder-${pet.id}`}>
                          <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity"></div>
                          <Beaker className="w-5 h-5 mr-2" />
                          <span className="font-semibold">Create Recipe</span>
                          <span className="ml-2 px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full animate-pulse">
                            Start Here!
                          </span>
                        </Link>
                      </Button>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <Button 
                      asChild
                      size="sm" 
                      className="bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
                    >
                      <Link to={`/ai-recommendations/${pet.id}`} data-testid={`ai-recommendations-${pet.id}`}>
                        <Zap className="w-4 h-4 mr-1" />
                        AI Suggestions
                      </Link>
                    </Button>
                    <Button 
                      asChild
                      size="sm" 
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    >
                      <Link to={`/saved-recipes/${pet.id}`} data-testid={`saved-recipes-${pet.id}`}>
                        <BookOpen className="w-4 h-4 mr-1" />
                        Saved Recipes
                      </Link>
                    </Button>
                    
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDeletePet(pet.id, pet.name)}
                      data-testid={`delete-pet-${pet.id}`}
                      className="col-span-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          </>
        )}
      </div>

      {/* Add Pet Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Pet Profile</DialogTitle>
            <DialogDescription>
              Create a profile for your pet to get personalized nutrition recommendations.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Pet Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Enter your pet's name"
                required
                data-testid="pet-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="pet_type">Pet Type</Label>
              <Select onValueChange={(value) => setFormData({...formData, pet_type: value})} required>
                <SelectTrigger data-testid="pet-type-select">
                  <SelectValue placeholder="Select pet type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dog">🐕 Dog</SelectItem>
                  <SelectItem value="cat">🐱 Cat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="breed">Breed</Label>
              <Input
                id="breed"
                value={formData.breed}
                onChange={(e) => setFormData({...formData, breed: e.target.value})}
                placeholder="e.g., Labrador, Persian"
                required
                data-testid="breed-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="age_months">Age (months)</Label>
                <Input
                  id="age_months"
                  type="number"
                  value={formData.age_months}
                  onChange={(e) => setFormData({...formData, age_months: e.target.value})}
                  placeholder="24"
                  required
                  data-testid="age-input"
                />
              </div>
              
              <div>
                <Label htmlFor="weight_kg">Weight (kg)</Label>
                <Input
                  id="weight_kg"
                  type="number"
                  step="0.1"
                  min="0.1"
                  max={formData.pet_type === 'cat' ? "8" : "100"}
                  value={formData.weight_kg}
                  onChange={(e) => setFormData({...formData, weight_kg: e.target.value})}
                  placeholder={formData.pet_type === 'cat' ? "Max 8kg for cats" : "15.5"}
                  required
                  data-testid="weight-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="activity_level">Activity Level</Label>
              <Select onValueChange={(value) => setFormData({...formData, activity_level: value})} required>
                <SelectTrigger data-testid="activity-select">
                  <SelectValue placeholder="Select activity level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - Indoor, minimal exercise</SelectItem>
                  <SelectItem value="moderate">Moderate - Regular walks</SelectItem>
                  <SelectItem value="high">High - Very active, lots of exercise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600" data-testid="create-pet-submit">
                Create Profile
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Nutrition Requirements Dialog */}
      <Dialog open={!!selectedPet} onOpenChange={() => setSelectedPet(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedPet?.name}'s Daily Nutrition Requirements</DialogTitle>
            <DialogDescription>
              Professional nutritional recommendations based on AAFCO standards
            </DialogDescription>
          </DialogHeader>
          
          {requirements && (
            <div className="space-y-4" data-testid="nutrition-requirements">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">Calories</p>
                  <p className="text-lg font-bold text-green-800">{Math.round(requirements.calories)} kcal</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <p className="text-sm text-orange-600 font-medium">Protein</p>
                  <p className="text-lg font-bold text-orange-800">{Math.round(requirements.protein_grams)}g</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-yellow-600 font-medium">Fat</p>
                  <p className="text-lg font-bold text-yellow-800">{Math.round(requirements.fat_grams)}g</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">Carbs</p>
                  <p className="text-lg font-bold text-blue-800">{Math.round(requirements.carbohydrates_grams)}g</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Additional Requirements</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Calcium: {Math.round(requirements.calcium_mg)}mg</div>
                  <div>Phosphorus: {Math.round(requirements.phosphorus_mg)}mg</div>
                  <div>Fiber: {Math.round(requirements.fiber_grams)}g</div>
                  <div>Water: {Math.round(requirements.water_ml)}ml</div>
                </div>
              </div>
              
              {/* AAFCO Information Footer */}
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">i</span>
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-blue-800 mb-1">Based on AAFCO Standards</p>
                    <p className="text-blue-700 text-xs leading-relaxed">
                      These nutritional requirements follow guidelines from the <strong>Association of American Feed Control Officials (AAFCO)</strong>, 
                      the recognized authority for pet food nutrition standards in North America.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Payment Modal */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
      />
      </div>
      </>
    </ProtectedRoute>
  );
};

// Recipe Builder Page
const RecipeBuilderPage = () => {
  const [pet, setPet] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { petId } = useParams();
  
  useEffect(() => {
    fetchPetAndRequirements();
  }, [petId]);

  const fetchPetAndRequirements = async () => {
    try {
      const [petResponse, reqResponse] = await Promise.all([
        axios.get(`${API}/pets/${petId}`),
        axios.get(`${API}/pets/${petId}/aafco-requirements`)
      ]);
      setPet(petResponse.data);
      setRequirements(reqResponse.data);
    } catch (error) {
      toast.error('Failed to load pet data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading recipe builder...</p>
        </div>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 flex items-center justify-center">
        <Card className="text-center p-8">
          <CardContent>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Pet not found</h3>
            <p className="text-gray-600 mb-4">The pet you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/pets">Go to My Pets</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
        <Navigation />

        <div className="max-w-7xl mx-auto px-4 py-8">
          <RecipeBuilder 
            petId={petId} 
            petName={pet.name} 
            requirements={requirements} 
            onUpgradePrompt={() => setShowPaymentModal(true)}
          />
        </div>
        
        {/* Payment Modal */}
        <PaymentModal 
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
        />
      </div>
    </ProtectedRoute>
  );
};

// AI Recommendations Page  
const AIRecommendationsPage = () => {
  const [pet, setPet] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAI, setLoadingAI] = useState(false);
  const { petId } = useParams();
  
  // Helper function to clean up enum values in text
  const cleanEnumText = (text) => {
    if (!text) return text;
    return text
      .replace(/ActivityLevel\.(LOW|MEDIUM|HIGH)/g, (match, level) => level.toLowerCase())
      .replace(/PetType\.(DOG|CAT)s?/gi, (match, type) => type.toLowerCase() + 's')
      .replace(/PetType\.(DOG|CAT)/gi, (match, type) => type.toLowerCase())
      .replace(/LifeStage\.(PUPPY|KITTEN|ADULT|SENIOR)/g, (match, stage) => stage.toLowerCase())
      .replace(/-energy/g, ' energy')
      .replace(/\b([a-z])([A-Z])/g, '$1 $2'); // Add space between camelCase
  };
  
  useEffect(() => {
    fetchPet();
  }, [petId]);

  const fetchPet = async () => {
    try {
      const response = await axios.get(`${API}/pets/${petId}`);
      setPet(response.data);
    } catch (error) {
      toast.error('Failed to load pet data');
    } finally {
      setLoading(false);
    }
  };

  const getRecommendations = async () => {
    setLoadingAI(true);
    try {
      const response = await axios.post(`${API}/ai-recommendations`, {
        pet_id: petId,
        preferences: [],
        avoid_ingredients: [],
        max_prep_time: 60
      });
      setRecommendations(response.data.recommendations);
      toast.success('AI recommendations generated!');
    } catch (error) {
      toast.error('Failed to get AI recommendations');
    } finally {
      setLoadingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading AI assistant...</p>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
        <Navigation />

        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              ✨ AI Recipe Recommendations for {pet?.name}
          </h1>
          <p className="text-xl text-gray-600">
            Get personalized recipe suggestions using GPT-5 and Indian ingredients
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Pet Profile Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {pet && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p><span className="font-medium">Type:</span> {pet.pet_type === 'dog' ? '🐕 Dog' : '🐱 Cat'}</p>
                  <p><span className="font-medium">Breed:</span> {pet.breed}</p>
                  <p><span className="font-medium">Age:</span> {Math.floor(pet.age_months / 12)}y {pet.age_months % 12}m</p>
                </div>
                <div>
                  <p><span className="font-medium">Weight:</span> {pet.weight_kg} kg</p>
                  <p><span className="font-medium">Activity:</span> {pet.activity_level}</p>
                  <p><span className="font-medium">Life Stage:</span> {pet.life_stage.replace('_', ' ')}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center mb-6">
          <Button 
            onClick={getRecommendations} 
            disabled={loadingAI}
            size="lg"
            className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
            data-testid="generate-ai-recommendations"
          >
            {loadingAI ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating Recommendations...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5 mr-2" />
                Get AI Recipe Suggestions
              </>
            )}
          </Button>
        </div>

        {recommendations.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 text-center">Your Personalized Recommendations</h2>
            {recommendations.map((recommendation, index) => (
              <Card key={index} className="border-l-4 border-l-blue-500">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      {typeof recommendation === 'string' ? (
                        <p className="text-gray-700 leading-relaxed">{recommendation}</p>
                      ) : (
                        <div>
                          <h3 className="font-semibold text-lg text-gray-800 mb-2">{cleanEnumText(recommendation.title)}</h3>
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line">{cleanEnumText(recommendation.description)}</p>
                          {recommendation.ingredients && (
                            <div className="mt-3">
                              <span className="text-sm font-medium text-gray-600">Key ingredients: </span>
                              <span className="text-sm text-gray-700">
                                {Array.isArray(recommendation.ingredients) 
                                  ? recommendation.ingredients.map(ing => typeof ing === 'string' ? ing : ing.name).join(', ')
                                  : 'chicken, rice, vegetables'
                                }
                              </span>
                            </div>
                          )}
                          
                          {recommendation.safety_note && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <span className="text-sm text-green-800">✅ {cleanEnumText(recommendation.safety_note)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            <div className="text-center mt-6">
              <Button asChild variant="outline">
                <Link to={`/recipe-builder/${petId}`}>
                  <Beaker className="w-4 h-4 mr-2" />
                  Try Recipe Builder
                </Link>
              </Button>
            </div>
          </div>
        )}

        {!loadingAI && recommendations.length === 0 && (
          <Card className="text-center p-8">
            <CardContent>
              <Zap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Ready for AI Magic!</h3>
              <p className="text-gray-600">
                Click the button above to get personalized recipe recommendations tailored specifically for {pet?.name}
              </p>
            </CardContent>
          </Card>
        )}
        </div>
      </div>
    </ProtectedRoute>
  );
};

// Recipe Browse Page
const RecipeBrowsePage = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    pet_type: '',
    difficulty: '',
    life_stage: '',
    max_prep_time: ''
  });
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  useEffect(() => {
    fetchRecipes();
  }, [filters]);

  const fetchRecipes = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value);
      });
      
      const response = await axios.get(`${API}/recipes?${params.toString()}`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Recipe fetch error:', error);
      toast.error('Failed to fetch recipes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50" data-testid="recipe-browse-page">
      <Helmet>
        <title>Indian Pet Recipes | AAFCO-Compliant Dog & Cat Food Recipes | Desi Pet Recipes</title>
        <meta name="description" content="Browse 100+ AAFCO-compliant homemade pet food recipes using Indian ingredients. Filter by pet type, difficulty, age, and cooking time. Healthy recipes for dogs and cats." />
        <meta name="keywords" content="indian pet recipes, dog food recipes, cat food recipes, homemade pet food, AAFCO recipes, pet nutrition India" />
        <link rel="canonical" href="https://desipetrecipes.in/recipes" />
        
        {/* ItemList Schema for Recipe Listing */}
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Indian Pet Food Recipes",
          "description": "AAFCO-compliant homemade pet food recipes using Indian ingredients",
          "numberOfItems": recipes.length,
          "itemListElement": recipes.slice(0, 10).map((recipe, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Recipe",
              "@id": `https://desipetrecipes.in/recipes/${recipe.id}`,
              "name": recipe.title,
              "description": recipe.description,
              "image": recipe.image_url,
              "recipeCategory": "Pet Food",
              "recipeCuisine": "Indian",
              "keywords": recipe.tags?.join(', '),
              "prepTime": `PT${recipe.prep_time_minutes}M`,
              "cookTime": `PT${recipe.cook_time_minutes}M`,
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": recipe.rating,
                "reviewCount": recipe.rating_count
              }
            }
          }))
        })}
        </script>
      </Helmet>
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Indian Pet Recipes</h1>
          <p className="text-lg text-gray-600 mb-6">
            Discover over 100 nutritious, AAFCO-compliant homemade pet food recipes tailored for Indian kitchens. 
            Each recipe uses locally available ingredients like dal, rice, chicken, vegetables, and paneer—making healthy pet nutrition affordable and accessible. 
            Our recipes are scientifically balanced to meet your pet's daily nutritional requirements while using familiar Indian cooking methods.
          </p>
          
          {/* Filters */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-700">Filter Recipes</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Select onValueChange={(value) => setFilters({...filters, pet_type: value})}>
                <SelectTrigger data-testid="filter-pet-type">
                  <SelectValue placeholder="Pet Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Pets</SelectItem>
                  <SelectItem value="dog">🐕 Dogs</SelectItem>
                  <SelectItem value="cat">🐱 Cats</SelectItem>
                </SelectContent>
              </Select>
              
              <Select onValueChange={(value) => setFilters({...filters, difficulty: value})}>
                <SelectTrigger data-testid="filter-difficulty">
                  <SelectValue placeholder="Difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Difficulty</SelectItem>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
              
              <Select onValueChange={(value) => setFilters({...filters, life_stage: value})}>
                <SelectTrigger data-testid="filter-life-stage">
                  <SelectValue placeholder="Life Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Age</SelectItem>
                  <SelectItem value="puppy_kitten">Puppy/Kitten</SelectItem>
                  <SelectItem value="adult">Adult</SelectItem>
                  <SelectItem value="senior">Senior</SelectItem>
                </SelectContent>
              </Select>
              
              <Select onValueChange={(value) => setFilters({...filters, max_prep_time: value})}>
                <SelectTrigger data-testid="filter-prep-time">
                  <SelectValue placeholder="Prep Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Time</SelectItem>
                  <SelectItem value="15">Under 15 min</SelectItem>
                  <SelectItem value="30">Under 30 min</SelectItem>
                  <SelectItem value="60">Under 1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Card>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading delicious recipes...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="recipe-grid">
              {recipes.map((recipe) => (
              <Card key={recipe.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`recipe-card-${recipe.id}`}>
                <div className="relative h-48">
                  <img 
                    src={recipe.image_url || (recipe.pet_type === 'dog' ? 
                      "https://images.unsplash.com/photo-1676193866128-03a926df76ef" : 
                      "https://images.unsplash.com/photo-1589924691995-400dc9ecc119"
                    )}
                    alt={recipe.title}
                    className="w-full h-full object-cover"
                  />
                  <Badge className={`absolute top-4 left-4 ${
                    recipe.pet_type === 'dog' ? 'bg-green-600' : 'bg-orange-600'
                  } text-white`}>
                    {recipe.pet_type === 'dog' ? '🐕 Dog' : '🐱 Cat'}
                  </Badge>
                  <Badge className="absolute top-4 right-4 bg-white/90 text-gray-700">
                    {recipe.difficulty}
                  </Badge>
                </div>
                
                <CardHeader>
                  <CardTitle className="text-lg">{recipe.title}</CardTitle>
                  <CardDescription className="line-clamp-2">{recipe.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {recipe.prep_time_minutes + recipe.cook_time_minutes} mins
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      {recipe.rating} ({recipe.rating_count})
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {recipe.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
                    onClick={() => setSelectedRecipe(recipe)}
                    data-testid={`view-recipe-${recipe.id}`}
                  >
                    View Recipe
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {!loading && recipes.length === 0 && (
          <Card className="text-center p-8">
            <CardContent>
              <ChefHat className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No recipes found</h3>
              <p className="text-gray-600">Try adjusting your filters to find more recipes.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recipe Detail Dialog */}
      <Dialog open={!!selectedRecipe} onOpenChange={() => setSelectedRecipe(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedRecipe && (
            <div data-testid="recipe-detail">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={selectedRecipe.pet_type === 'dog' ? 'bg-green-600' : 'bg-orange-600'}>
                    {selectedRecipe.pet_type === 'dog' ? '🐕 Dog' : '🐱 Cat'}
                  </Badge>
                  <Badge variant="outline">{selectedRecipe.difficulty}</Badge>
                  <Badge variant="outline">{selectedRecipe.prep_time_minutes + selectedRecipe.cook_time_minutes} mins</Badge>
                </div>
                <DialogTitle className="text-2xl">{selectedRecipe.title}</DialogTitle>
                <DialogDescription className="text-base">
                  {selectedRecipe.description}
                </DialogDescription>
              </DialogHeader>
              
              <Tabs defaultValue="ingredients" className="mt-6">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="ingredients">Ingredients</TabsTrigger>
                  <TabsTrigger value="instructions">Instructions</TabsTrigger>
                  <TabsTrigger value="nutrition">Nutrition</TabsTrigger>
                  <TabsTrigger value="tips">Tips</TabsTrigger>
                </TabsList>
                
                <TabsContent value="ingredients" className="space-y-3">
                  <h3 className="font-semibold">Ingredients (serves {selectedRecipe.servings})</h3>
                  <div className="space-y-2">
                    {selectedRecipe.ingredients.map((ingredient, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">{ingredient.name}</span>
                          {ingredient.indian_name && (
                            <span className="text-sm text-gray-600 ml-2">({ingredient.indian_name})</span>
                          )}
                        </div>
                        <span className="font-semibold text-green-600">{ingredient.quantity}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                
                <TabsContent value="instructions" className="space-y-3">
                  <h3 className="font-semibold">Cooking Instructions</h3>
                  <div className="space-y-2">
                    {selectedRecipe.instructions.map((instruction, index) => (
                      <div key={index} className="flex gap-3">
                        <div className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {index + 1}
                        </div>
                        <p className="text-gray-700">{instruction}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                
                <TabsContent value="nutrition" className="space-y-3">
                  <h3 className="font-semibold">Nutritional Information (per serving)</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-green-50 p-3 rounded">
                      <p className="text-sm text-green-600">Calories</p>
                      <p className="font-bold text-green-800">{selectedRecipe.nutritional_info_per_serving.calories} kcal</p>
                    </div>
                    <div className="bg-orange-50 p-3 rounded">
                      <p className="text-sm text-orange-600">Protein</p>
                      <p className="font-bold text-orange-800">{selectedRecipe.nutritional_info_per_serving.protein}g</p>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded">
                      <p className="text-sm text-yellow-600">Fat</p>
                      <p className="font-bold text-yellow-800">{selectedRecipe.nutritional_info_per_serving.fat}g</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded">
                      <p className="text-sm text-blue-600">Carbs</p>
                      <p className="font-bold text-blue-800">{selectedRecipe.nutritional_info_per_serving.carbohydrates}g</p>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded">
                    <h4 className="font-medium text-gray-800 mb-2">Additional Nutrients</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Fiber: {selectedRecipe.nutritional_info_per_serving.fiber}g</div>
                      <div>Calcium: {selectedRecipe.nutritional_info_per_serving.calcium}mg</div>
                      <div>Phosphorus: {selectedRecipe.nutritional_info_per_serving.phosphorus}mg</div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="tips" className="space-y-3">
                  {selectedRecipe.benefits.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-green-700 mb-2">Benefits</h3>
                      <ul className="space-y-1">
                        {selectedRecipe.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-green-500 mt-1">✓</span>
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {selectedRecipe.precautions.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-orange-700 mb-2">Precautions</h3>
                      <ul className="space-y-1">
                        {selectedRecipe.precautions.map((precaution, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-orange-500 mt-1">⚠</span>
                            <span className="text-gray-700">{precaution}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// MyBooksPage component removed - functionality merged into "Buy Recipe Books" page

// Analytics tracking component
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

// Recipe Book Landing Page (for WhatsApp/Facebook sharing)
const RecipeBookLandingPage = () => {
  const navigate = useNavigate();
  
  // Features for the "Why Choose Our Recipe Book?" section
  const features = [
    { icon: "🐕", title: "50 Complete Recipes", desc: "Full meals for dogs, balanced and nutritious" },
    { icon: "✅", title: "AAFCO Compliant", desc: "Meets all nutritional standards for pet health" },
    { icon: "🇮🇳", title: "Indian Ingredients", desc: "Easy to find, affordable local ingredients" },
    { icon: "🌍", title: "International Option", desc: "Global ingredients version available" },
    { icon: "📱", title: "Instant Download", desc: "Get your PDF immediately after payment" },
    { icon: "📧", title: "Email Delivery", desc: "Also sent to your email for safekeeping" }
  ];
  
  const handleGetStarted = () => {
    // Scroll to top before navigating
    window.scrollTo({ top: 0, behavior: 'instant' });
    // Navigate directly to checkout - no login required!
    navigate('/generic-recipe-checkout');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-orange-50 to-yellow-50">
      <Helmet>
        <title>Buy Recipe Books - Indian & International | Desi Pet Recipes</title>
        <meta name="description" content="Get 50 AAFCO-compliant pet food recipes. Indian version ₹149, International version $2.99. Instant PDF download!" />
      </Helmet>
      
      <Navigation showAuth={true} />
      
      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full font-semibold text-sm mb-6">
            📚 Limited Time Offer - 50% OFF
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-6">
            Get <span className="text-orange-600">50 Generic</span><br />
            Pet Food Recipes
          </h1>
          <p className="text-xl text-gray-600 mb-10">
            Professional recipe book • AAFCO-compliant • Instant PDF download
          </p>
          
          {/* Two Books Side by Side */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-10">
            {/* Indian Version */}
            <div className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow">
              <div className="relative mb-4">
                <img 
                  src="/images/book_cover.jpg" 
                  alt="Indian Recipe Book Cover"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
                <span className="absolute top-3 left-3 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  🇮🇳 INDIAN
                </span>
                <span className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  50% OFF
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Indian Version</h3>
              <p className="text-gray-600 mb-4">50 recipes with locally available Indian ingredients</p>
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-4xl font-bold text-orange-600">₹149</span>
                <span className="text-xl text-gray-400 line-through">₹299</span>
              </div>
              <ul className="text-sm text-left space-y-2 mb-4">
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> 50 complete recipes</li>
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Indian ingredients</li>
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> AAFCO compliant</li>
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> PDF sent to email</li>
              </ul>
            </div>
            
            {/* International Version */}
            <div className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow">
              <div className="relative mb-4">
                <img 
                  src="/images/international_book_cover.jpg" 
                  alt="International Recipe Book Cover"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
                <span className="absolute top-3 left-3 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  🌍 INTERNATIONAL
                </span>
                <span className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  50% OFF
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">International Version</h3>
              <p className="text-gray-600 mb-4">50 recipes with globally available ingredients</p>
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-4xl font-bold text-blue-600">$2.99</span>
                <span className="text-xl text-gray-400 line-through">$5.99</span>
              </div>
              <ul className="text-sm text-left space-y-2 mb-4">
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> 50 complete recipes</li>
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Global ingredients</li>
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> AAFCO compliant</li>
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> PDF sent to email</li>
              </ul>
            </div>
          </div>
          
          <Button
            size="lg"
            onClick={handleGetStarted}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-xl px-12 py-8 rounded-full shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all"
          >
            🛒 Buy Now - Choose Your Version
          </Button>
          
          <p className="text-sm text-gray-500 mt-4">
            ⚡ Instant download • 📧 PDF sent to email • 💳 Secure payment
          </p>
        </div>
      </section>
      
      {/* Sample Preview Section */}
      <section className="py-12 px-4 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            📖 What's Inside the Recipe Book?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">1️⃣</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800 mb-2">50 Complete Recipes</h3>
                  <p className="text-gray-600">Each with detailed ingredients, instructions, and nutritional breakdown</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">2️⃣</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800 mb-2">Professional Cover</h3>
                  <p className="text-gray-600">Beautiful design ready for your reference library</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">3️⃣</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800 mb-2">AAFCO Nutrition Info</h3>
                  <p className="text-gray-600">Every recipe meets international pet nutrition standards</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex-shrink-0 flex items-center justify-center">
                  <span className="text-2xl">4️⃣</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800 mb-2">Preparation Tips</h3>
                  <p className="text-gray-600">Expert guidance on ingredient sourcing and meal prep</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-red-100 p-8 rounded-2xl shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=600&h=400&fit=crop"
                alt="Homemade pet food recipes - healthy ingredients"
                className="rounded-lg shadow-lg mb-4"
              />
              <p className="text-center text-gray-700 font-semibold">Professional PDF Format • Print or Digital</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Grid */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            Why Choose Our Recipe Book?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-12 px-4 bg-gradient-to-r from-orange-50 to-red-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            📝 How It Works (3 Simple Steps)
          </h2>
          <div className="space-y-6">
            <div className="flex items-center gap-6 bg-white p-6 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="font-bold text-xl text-gray-800 mb-1">Click "Buy Now"</h3>
                <p className="text-gray-600">No account needed - just click and pay</p>
              </div>
            </div>
            <div className="flex items-center gap-6 bg-white p-6 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="font-bold text-xl text-gray-800 mb-1">Pay Securely</h3>
                <p className="text-gray-600">Indian version ₹149 • International coming soon</p>
              </div>
            </div>
            <div className="flex items-center gap-6 bg-white p-6 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="font-bold text-xl text-gray-800 mb-1">Download Instantly</h3>
                <p className="text-gray-600">Enter your email & get your PDF immediately!</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Copyright & Usage Terms Section */}
      <section className="py-12 px-4 bg-red-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border-2 border-red-200 rounded-xl p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-red-700 mb-2">Copyright & Usage Terms</h3>
                <p className="text-gray-700 font-semibold">Please read before purchasing</p>
              </div>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="font-semibold">
                This recipe book is copyrighted content © {new Date().getFullYear()} DesiPetRecipes.in. All rights reserved.
              </p>
              
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                <p className="font-semibold text-green-800 mb-2">✅ YOU MAY:</p>
                <ul className="list-disc list-inside space-y-1 text-green-700">
                  <li>Use recipes to prepare meals for your own pet(s)</li>
                  <li>Print one copy for personal use</li>
                  <li>Store on your personal devices</li>
                </ul>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <p className="font-semibold text-red-800 mb-2">❌ YOU MAY NOT:</p>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>Share on WhatsApp, Instagram, Telegram, or any social media</li>
                  <li>Upload to websites, blogs, or file-sharing platforms</li>
                  <li>Send to friends, family, or any third party</li>
                  <li>Reproduce, distribute, or use commercially</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-300 p-4 rounded">
                <p className="text-sm text-gray-800">
                  <strong>⚖️ Legal Notice:</strong> Unauthorized sharing or reproduction is <strong>copyright infringement</strong> under the <strong>Indian Copyright Act, 1957</strong> and may result in legal action. Each purchase is licensed to one buyer only and includes a personalized watermark with your name and email for protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-orange-500 to-red-500 p-12 rounded-3xl shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            🎉 Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of pet parents giving their pets healthy, homemade meals!
          </p>
          <Button
            size="lg"
            onClick={handleGetStarted}
            className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-12 py-8 rounded-full shadow-xl transform hover:scale-105 transition-all font-bold"
          >
            Get My 50 Recipe Book Now →
          </Button>
          <p className="text-white/80 text-sm mt-6">
            ✅ Instant Access • 💳 Secure Checkout • 🔒 Money-Back Guarantee
          </p>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

// Generic Recipe Book Checkout Page - GUEST CHECKOUT (Pay first, then email)
const GenericRecipeCheckoutPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState('indian');
  const [step, setStep] = useState('checkout'); // checkout, email, success
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [error, setError] = useState(null);
  
  // Scroll to top when component mounts or step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);
  
  // On mount, check URL params (from email link) or sessionStorage
  useEffect(() => {
    const checkStatus = async () => {
      // First check URL params (user clicked link in email)
      const urlParams = new URLSearchParams(window.location.search);
      const urlPurchaseId = urlParams.get('purchase_id');
      const urlStatus = urlParams.get('status');
      
      if (urlPurchaseId && urlStatus === 'success') {
        // User came from email link - fetch purchase details
        try {
          const response = await axios.get(`${API}/recipe-book/guest-purchase-status/${urlPurchaseId}`);
          if (response.data.payment_status === 'completed') {
            sessionStorage.setItem('completedPurchaseId', urlPurchaseId);
            if (response.data.buyer_email) {
              sessionStorage.setItem('completedPurchaseEmail', response.data.buyer_email);
              setBuyerEmail(response.data.buyer_email);
            }
            setStep('success');
          }
        } catch (err) {
          console.error('Error fetching purchase:', err);
          toast.error('Purchase not found or not completed');
        }
        return;
      }
      
      // Check if we have completed purchase in sessionStorage
      const completedId = sessionStorage.getItem('completedPurchaseId');
      if (completedId) {
        const savedEmail = sessionStorage.getItem('completedPurchaseEmail');
        if (savedEmail) setBuyerEmail(savedEmail);
        setStep('success');
        return;
      }
      
      // Check for pending payment data
      const savedData = sessionStorage.getItem('guestPurchaseData');
      if (savedData) {
        const data = JSON.parse(savedData);
        if (data.razorpay_payment_id) {
          setStep('email');
        }
      }
    };
    
    checkStatus();
  }, []);
  
  // Handle payment
  const handlePayment = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Create guest order
      const response = await axios.post(`${API}/recipe-book/guest-create-order`, {
        version: selectedVersion,
        origin_url: window.location.origin
      });
      
      const { order_id, amount, currency, key_id, purchase_id } = response.data;
      
      // Open Razorpay
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "Desi Pet Recipes",
        description: `50 Generic Recipe Book - ${selectedVersion === 'indian' ? 'Indian' : 'International'} Version`,
        order_id: order_id,
        handler: function(razorpayResponse) {
          // Save ALL payment data to sessionStorage (survives React state issues)
          const paymentData = {
            purchase_id: purchase_id,
            order_id: order_id,
            razorpay_order_id: razorpayResponse.razorpay_order_id,
            razorpay_payment_id: razorpayResponse.razorpay_payment_id,
            razorpay_signature: razorpayResponse.razorpay_signature,
            version: selectedVersion
          };
          sessionStorage.setItem('guestPurchaseData', JSON.stringify(paymentData));
          
          // Move to email collection step
          setStep('email');
          setLoading(false);
          toast.success('Payment successful! Please enter your email.', { duration: 5000 });
        },
        theme: { color: '#f97316' },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (err) {
      console.error('Payment error:', err);
      setError('Failed to initiate payment. Please try again.');
      toast.error('Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle email submission and complete purchase
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!buyerEmail || !buyerEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Get payment data from sessionStorage
    const savedData = sessionStorage.getItem('guestPurchaseData');
    
    if (!savedData) {
      setError('Payment data not found. Please make the payment again.');
      toast.error('Payment data not found. Please try again.');
      setStep('checkout');
      return;
    }
    
    const paymentData = JSON.parse(savedData);
    setLoading(true);
    setError(null);
    
    try {
      // Call backend to verify and complete
      const response = await axios.post(`${API}/recipe-book/guest-verify-and-complete`, {
        purchase_id: paymentData.purchase_id,
        razorpay_order_id: paymentData.razorpay_order_id,
        razorpay_payment_id: paymentData.razorpay_payment_id,
        razorpay_signature: paymentData.razorpay_signature,
        email: buyerEmail,
        phone: buyerPhone || null,
        name: buyerName || null
      });
      
      if (response.data.success) {
        // Clear sessionStorage
        sessionStorage.removeItem('guestPurchaseData');
        // Store purchase_id for download
        sessionStorage.setItem('completedPurchaseId', paymentData.purchase_id);
        sessionStorage.setItem('completedPurchaseEmail', buyerEmail);
        // Go to success step
        setStep('success');
        toast.success('Your recipe book is ready!', { duration: 5000 });
      } else {
        throw new Error('Verification failed');
      }
    } catch (err) {
      console.error('Verification error:', err);
      const errorMsg = err.response?.data?.detail || 'Verification failed. Please contact support.';
      setError(errorMsg);
      toast.error(errorMsg, { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle download - Direct PDF link using the correct working endpoint
  const handleDownload = () => {
    // Get the version from sessionStorage
    const savedData = sessionStorage.getItem('guestPurchaseData');
    let version = 'indian';
    if (savedData) {
      const data = JSON.parse(savedData);
      version = data.version || 'indian';
    }
    
    // Use the working direct download endpoint with version query param
    const downloadUrl = `${API}/download-recipe-book-direct?version=${version}`;
    window.open(downloadUrl, '_blank');
    toast.success('Opening recipe book...');
  };
  
  // STEP 3: Success/Download Page
  if (step === 'success') {
    const email = sessionStorage.getItem('completedPurchaseEmail') || buyerEmail;
    // Get version from sessionStorage
    const savedData = sessionStorage.getItem('guestPurchaseData');
    let purchaseVersion = selectedVersion;
    if (savedData) {
      const data = JSON.parse(savedData);
      purchaseVersion = data.version || 'indian';
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-orange-50 to-yellow-50">
        <Helmet>
          <title>Download Your Recipe Book | Desi Pet Recipes</title>
        </Helmet>
        <Navigation showAuth={true} />
        
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-8 rounded-2xl shadow-xl mb-8 text-center">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h1 className="text-3xl font-bold">Payment Successful!</h1>
            <p className="mt-2">Your recipe book is ready to download</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {purchaseVersion === 'international' ? '🌍' : '🇮🇳'} 50 Generic Recipe Book
            </h2>
            <p className="text-gray-600 mb-2">
              {purchaseVersion === 'international' ? 'International' : 'Indian'} Version • AAFCO Compliant
            </p>
            {email && <p className="text-sm text-green-600 mb-4">✅ Confirmation & PDF sent to: {email}</p>}
            
            <Button
              onClick={handleDownload}
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-xl py-6 rounded-xl font-bold"
            >
              {loading ? 'Preparing Download...' : '📥 Download Recipe Book (PDF)'}
            </Button>
            
            <p className="text-sm text-gray-500 mt-6">
              📧 PDF also sent to your email • Issues? Email: aaglobal2023@gmail.com
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  // STEP 2: Email Collection Page (after payment)
  if (step === 'email') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-orange-50 to-yellow-50">
        <Helmet>
          <title>Enter Your Email | Desi Pet Recipes</title>
        </Helmet>
        <Navigation showAuth={true} />
        
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-2xl shadow-xl mb-8 text-center">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h1 className="text-2xl font-bold">Payment Successful!</h1>
            <p>One more step - enter your email below</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">📧 Enter Your Email</h2>
            
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={buyerEmail}
                  onChange={(e) => setBuyerEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 text-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
                <Input
                  type="tel"
                  placeholder="+91 9876543210"
                  value={buyerPhone}
                  onChange={(e) => setBuyerPhone(e.target.value)}
                  className="w-full px-4 py-3"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name (Optional)</label>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  className="w-full px-4 py-3"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading || !buyerEmail}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-lg py-6 rounded-xl font-bold"
              >
                {loading ? 'Processing...' : '📥 Get My Recipe Book'}
              </Button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  // STEP 1: Checkout Page (select version, pay)
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50">
      <Helmet>
        <title>Buy Recipe Book | Desi Pet Recipes</title>
      </Helmet>
      <Navigation showAuth={true} />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
          🛒 Choose Your Recipe Book
        </h1>
        <p className="text-center text-gray-600 mb-8">Select the version that suits your needs</p>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}
        
        {/* Two Books Side by Side */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Indian Version */}
          <div 
            className={`bg-white rounded-2xl shadow-xl p-6 cursor-pointer transition-all ${selectedVersion === 'indian' ? 'ring-4 ring-orange-500 scale-[1.02]' : 'hover:shadow-2xl'}`}
            onClick={() => setSelectedVersion('indian')}
          >
            <div className="flex justify-between items-start mb-4">
              <span className="text-4xl">🇮🇳</span>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedVersion === 'indian' ? 'bg-orange-500 border-orange-500' : 'border-gray-300'}`}>
                {selectedVersion === 'indian' && <span className="text-white text-sm">✓</span>}
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-1">Indian Version</h2>
            <p className="text-gray-600 mb-4">50 recipes with Indian ingredients</p>
            
            <div className="bg-gradient-to-br from-orange-100 to-red-100 rounded-xl p-4 mb-4">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-orange-600">₹149</span>
                <span className="text-gray-500 line-through">₹299</span>
                <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">50% OFF</span>
              </div>
            </div>
            
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> 50 complete recipes</li>
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Indian ingredients</li>
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> AAFCO compliant</li>
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Instant PDF download</li>
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> PDF sent to email</li>
            </ul>
          </div>
          
          {/* International Version */}
          <div 
            className={`bg-white rounded-2xl shadow-xl p-6 cursor-pointer transition-all ${selectedVersion === 'international' ? 'ring-4 ring-blue-500 scale-[1.02]' : 'hover:shadow-2xl'}`}
            onClick={() => setSelectedVersion('international')}
          >
            <div className="flex justify-between items-start mb-4">
              <span className="text-4xl">🌍</span>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedVersion === 'international' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                {selectedVersion === 'international' && <span className="text-white text-sm">✓</span>}
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-1">International Version</h2>
            <p className="text-gray-600 mb-4">50 recipes with global ingredients</p>
            
            <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl p-4 mb-4">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-blue-600">$2.99</span>
                <span className="text-gray-500 line-through">$5.99</span>
                <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">50% OFF</span>
              </div>
            </div>
            
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> 50 complete recipes</li>
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Global ingredients</li>
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> AAFCO compliant</li>
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Instant PDF download</li>
              <li className="flex items-center gap-2"><span className="text-green-500">✓</span> PDF sent to email</li>
            </ul>
          </div>
        </div>
        
        {/* Payment Summary */}
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-semibold">Selected:</span>
            <span className="font-bold text-lg">
              {selectedVersion === 'indian' ? '🇮🇳 Indian Version' : '🌍 International Version'}
            </span>
          </div>
          
          <div className="flex justify-between items-center mb-6 pb-4 border-b">
            <span className="text-lg font-semibold">Total:</span>
            <span className="text-3xl font-bold text-orange-600">
              {selectedVersion === 'indian' ? '₹149' : '$2.99'}
            </span>
          </div>
          
          <Button
            onClick={handlePayment}
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-lg py-6 rounded-xl font-bold"
          >
            {loading ? 'Processing...' : '🔒 Proceed to Payment'}
          </Button>
          
          <p className="text-center text-sm text-gray-500 mt-4">
            🔒 Secure payment via Razorpay • PDF sent to your email
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Recipe Book Success Page
const RecipeBookSuccessPage = () => {
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(location.search);
      const orderId = params.get('order_id');
      
      if (!orderId) {
        setError('Invalid order');
        setLoading(false);
        return;
      }
      
      // Check purchase status
      try {
        const response = await axios.get(`${API}/pets`);
        // Get the purchase from recent purchases
        const purchaseResponse = await axios.get(`${API}/recipe-book/purchase/${orderId}`);
        
        if (purchaseResponse.data.payment_status === 'completed') {
          setPdfUrl(purchaseResponse.data.pdf_url);
          setLoading(false);
          toast.success('Payment successful! Your recipe book is ready!');
        } else {
          setError('Payment not completed yet. Please try again.');
          setLoading(false);
        }
      } catch (error) {
        console.error('Status check error:', error);
        setError('Failed to verify payment. Please check your purchases or contact support.');
        setLoading(false);
      }
    };
    
    checkPaymentStatus();
  }, [location]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Payment...</h2>
          <p className="text-gray-600">Please wait while we verify your payment</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 flex items-center justify-center">
        <Card className="max-w-2xl mx-4">
          <CardContent className="p-8">
            <div className="text-red-500 mb-4 text-center">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Payment Verification Issue</h2>
            <p className="text-gray-600 mb-6 text-center">{error}</p>
            
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
              <h3 className="font-bold text-orange-800 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Need Help? Contact Support
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                If your payment was successful but you're seeing this error, don't worry! Your payment is safe.
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <span className="font-semibold mr-2">Order ID:</span>
                  <code className="bg-white px-2 py-1 rounded text-xs">{new URLSearchParams(location.search).get('order_id') || 'N/A'}</code>
                </div>
                <div className="flex items-center text-sm">
                  <span className="font-semibold mr-2">Support Email:</span>
                  <a href="mailto:aaglobal2023@gmail.com" className="text-blue-600 hover:underline">aaglobal2023@gmail.com</a>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-4">
                💡 <strong>Tip:</strong> Include your Order ID when contacting support for faster resolution.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={() => navigate('/pets')} variant="outline" className="flex-1">
                Go to My Pets
              </Button>
              <Button onClick={() => window.location.href = 'mailto:aaglobal2023@gmail.com?subject=Recipe Book Issue&body=Order ID: ' + (new URLSearchParams(location.search).get('order_id') || 'N/A')} className="flex-1 bg-gradient-to-r from-orange-500 to-red-500">
                Email Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
      <Navigation />
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card className="text-center">
          <CardContent className="p-8">
            <div className="text-green-500 mb-4">
              <svg className="w-20 h-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">🎉 Payment Successful!</h1>
            <p className="text-lg text-gray-600 mb-6">
              Your personalized 50-recipe book is ready for download!
            </p>
            
            {pdfUrl && (
              <div className="space-y-4">
                <Button
                  size="lg"
                  onClick={() => window.open(pdfUrl, '_blank')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-lg px-8 py-6"
                >
                  <BookOpen className="w-6 h-6 mr-2" />
                  Download Recipe Book
                </Button>
                
                <p className="text-sm text-gray-500">
                  A copy has also been sent to your email
                </p>
              </div>
            )}
            
            <div className="mt-8 pt-8 border-t">
              <Button
                variant="outline"
                onClick={() => navigate('/pets')}
                className="mr-2"
              >
                Back to My Pets
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-green-500 to-orange-500"
              >
                Explore More Recipes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

function App() {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Initialize Google Analytics
    initGA();
    
    // DISABLED: Don't auto-show onboarding modal for anonymous visitors
    // This was blocking Sign In/Sign Up access
    // Onboarding will only show when triggered manually (e.g., after creating pet profile)
    // const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    // if (!hasSeenOnboarding) {
    //   setTimeout(() => setShowOnboarding(true), 2000);
    // }
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <AuthProvider>
          <BrowserRouter>
            <AppRouter showOnboarding={showOnboarding} setShowOnboarding={setShowOnboarding} />
          </BrowserRouter>
      </AuthProvider>
    </div>
    </HelmetProvider>
  );
}

// AppRouter Component - Handles Google OAuth detection during render
const AppRouter = ({ showOnboarding, setShowOnboarding }) => {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      }>
        <GoogleAuthCallback />
      </Suspense>
    );
  }
  
  // Normal routing
  return (
    <>
      <AnalyticsTracker />
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      }>
        <Routes>
          <Route path="/" element={<LandingPage setShowOnboarding={setShowOnboarding} />} />
            <Route path="/pets" element={<PetManagementPage />} />
            <Route path="/recipes" element={<RecipeBrowsePage />} />
            <Route path="/recipe-builder/:petId" element={<RecipeBuilderPage />} />
            <Route path="/ai-recommendations/:petId" element={
              <ProtectedRoute>
                <AIRecommendationsPage />
              </ProtectedRoute>
            } />
            <Route path="/special-diets" element={
              <ProtectedRoute>
                <SpecialDietsPage />
              </ProtectedRoute>
            } />
            <Route path="/saved-recipes/:petId" element={
              <ProtectedRoute>
                <SavedRecipesPage />
              </ProtectedRoute>
            } />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/test123" element={
              <div style={{
                padding: '50px', 
                backgroundColor: 'purple', 
                color: 'yellow', 
                fontSize: '24px',
                minHeight: '100vh',
                textAlign: 'center'
              }}>
                <h1>🚀 TEST PAGE WORKING!</h1>
                <p>If you see this purple page, deployments are working!</p>
                <p>URL: /test123</p>
              </div>
            } />
            <Route path="/admin" element={
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            } />
            <Route path="/admin/broadcast" element={
              <ProtectedAdminRoute>
                <AdminBroadcast />
              </ProtectedAdminRoute>
            } />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/recipe-book" element={<RecipeBookLandingPage />} />
            <Route path="/generic-recipe-checkout" element={<GenericRecipeCheckoutPage />} />
            <Route path="/recipe-book-success" element={<RecipeBookSuccessPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/meal-planner-preview" element={<MealPlannerPreview />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/vets" element={<VetLandingLazy />} />
            <Route path="/vets/preview" element={<VetPreviewLazy />} />
            <Route path="/vets/demo/:token" element={<VetDemoLazy />} />
            <Route path="/vets/join" element={<VetJoinLazy />} />
            <Route path="/vets/login" element={<VetLoginLazy />} />
            <Route path="/vets/dashboard" element={<VetDashboardLazy />} />
            <Route path="/vets/recipe-builder" element={<VetRecipeBuilderLazy />} />
            <Route path="/admin/vet-demos" element={<AdminVetDemosLazy />} />
            <Route path="/admin/vet-recipes" element={<AdminVetRecipesLazy />} />
            <Route path="/kibble-calculator" element={<KibblePortionCalculatorLazy />} />
            <Route path="/kibble/:slug" element={<KibbleSEOPageLazy />} />
            <Route path="/meal-plan-order" element={<MealPlanOrder />} />
            <Route path="/meal-plan-success" element={<MealPlanSuccess />} />
{/* My Books route removed - functionality merged into Buy Recipe Books page */}

          </Routes>
        </Suspense>
        <Footer />
        <AuthModal />
        <OnboardingModal 
          isOpen={showOnboarding}
          onClose={() => {
            setShowOnboarding(false);
            localStorage.setItem('hasSeenOnboarding', 'true');
          }}
          onComplete={() => {
            localStorage.setItem('hasSeenOnboarding', 'true');
          }}
        />
        
{/* Floating Support Widget removed */}
      </>
    );
};

// Floating Support Widget Component
const FloatingSupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Support Options Panel */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in slide-in-from-bottom-5 duration-200">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <HelpCircle className="w-5 h-5" />
              Need Help?
            </h3>
            <p className="text-green-100 text-sm">We're here to assist you!</p>
          </div>
          
          <div className="p-3">
            {/* Email */}
            <a 
              href="mailto:aaglobal2023@gmail.com?subject=Help%20Request%20-%20Desi%20Pet%20Recipes"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-green-50 transition-colors group"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200">
                <Mail className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="font-medium text-gray-800">Email Us</div>
                <div className="text-xs text-gray-500">aaglobal2023@gmail.com</div>
              </div>
            </a>
          </div>
          
          <div className="border-t p-3 bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              🕐 Typically respond within 2-4 hours
            </p>
          </div>
        </div>
      )}
      
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-gray-600 hover:bg-gray-700 rotate-45' 
            : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 hover:scale-110'
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>
      
      {/* Pulse animation when closed */}
      {!isOpen && (
        <span className="absolute -top-1 -right-1 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
        </span>
      )}
    </div>
  );
};

export default App;